import {
  AfterViewInit,
  Component,
  computed,
  effect,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  TemplateRef,
  ViewChild,
  WritableSignal,
  inject,
} from '@angular/core';
import { UpdatePageTitle } from '../../../../store/app/app.actions';
import { Store } from '@ngxs/store';
import { TransactionsApiService } from '../../../../_services/transactions-service/transactions-api.service';
import { AppTableComponent } from '../../../../shared/components/table/app-table.component';
import { TableColumn } from '@swimlane/ngx-datatable';
import { hasAccountingCategoryColumns, noAccountingCategoryColumns } from '../transactions/_utils/transactions-utils';
import { CategoriesSearchOptions } from '../../../../shared/components/forms/search-field/search-utils';
import { Observable, Subscription } from 'rxjs';
import {
  TransactionCategory,
  TransactionCategoryVisibilityRequest,
} from '../../../../_interfaces/transactions.interface';
import { PaginatedResults } from '@bo/ng-api';
import { HttpParams } from '@angular/common/http';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ToastService } from '../../../../_services/toast-service/toast.service';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { BoFormsModule } from '@bo/ng-forms';
import { ManageCategoryComponent } from './manage-category/manage-category.component';
import { DatatableComponent } from '@bo/ng-datatable';
import { EmptyIndicatorPipe } from '../../../../shared/pipes/empty-indicator.pipe';
import { SubscriptionSelectors } from '../../../../store/subscription/subscription.selectors';
import { TableHeaderTemplateComponent } from '../../../../shared/components/table-header-template/table-header-template.component';
import { NgClass } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AccountingConnectionStatus,
  AccountingSyncStatus,
  AccountingSyncType,
} from '../../../../_interfaces/accounting.interface';
import { AccountingService } from '../_services/accounting/accounting.service';
import {
  ClearCategoriesSyncStatus,
  StartPollingCategoriesSyncStatus,
} from '../../../../store/transactions/transactions.actions';
import { AccountingApiService } from '../../../../_services/accounting-service/accounting-api/accounting-api.service';
import { TransactionsSelectors } from '../../../../store/transactions/transactions.selectors';
import { TransactionsState } from '../../../../store/transactions/transactions.state';
import { SetAccountingProviderStateFromTransactionSyncFailure } from '../../../../store/accounting/accounting.actions';
import { updateParamsObject } from '@bo/ng-utils';
import { ToggleSwitchComponent } from '../../../../shared/components/forms/toggle-switch/toggle-switch.component';

@Component({
  selector: 'app-categories',
  imports: [
    AppTableComponent,
    RouterLink,
    BoFormsModule,
    EmptyIndicatorPipe,
    TableHeaderTemplateComponent,
    NgClass,
    NgbTooltip,
    ToggleSwitchComponent,
  ],
  templateUrl: './categories.component.html',
})
export class CategoriesComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly store = inject(Store);
  readonly transactionsApiService = inject(TransactionsApiService);
  readonly accountingService = inject(AccountingService);
  readonly accountingApiService = inject(AccountingApiService);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly toastService = inject(ToastService);
  readonly modalService = inject(NgbModal);
  readonly transactionsState = inject(TransactionsState);

  readonly hasAccounting$ = this.store.select(SubscriptionSelectors.hasAccounting);
  readonly busySyncing$ = this.store.select(TransactionsSelectors.busySyncing);
  readonly syncStatus$ = this.store.select(TransactionsSelectors.categoriesSyncStatus);

  private readonly columnsInitialized = signal(false);
  showInvisibleCategories = signal(false);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // TODO: TemplateRef<any> can be fixed by refactoring the @bo/ng-datatable library.
  @ViewChild('accountCell', { static: false }) accountCell!: TemplateRef<any>;
  @ViewChild('categoryNameCell', { static: false }) categoryNameCell!: TemplateRef<any>;
  @ViewChild('actionsCell', { static: false }) actionsCell!: TemplateRef<any>;
  @ViewChild('table', { static: false }) table!: AppTableComponent<DatatableComponent>;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  protected returnUrl: string = 'accounting/categories';
  columns: Array<TableColumn> = [];
  protected editingRowId = signal<number | null>(null);
  protected readonly hasAccounting = toSignal(this.hasAccounting$, { initialValue: false });
  protected hasActiveConnection: Signal<boolean> = computed(() => {
    return this.accountingService.connectionStatus() === AccountingConnectionStatus.Complete;
  });
  protected canAddCategory: Signal<boolean> = computed(() => {
    return !this.hasAccounting() || !this.hasActiveConnection();
  });
  readonly subscriptions = new Subscription();
  protected readonly CategoriesSearchOptions = CategoriesSearchOptions;

  busySyncing = signal(false);
  syncStatus: WritableSignal<AccountingSyncStatus | undefined> = signal(undefined);

  constructor() {
    this.store.dispatch(new UpdatePageTitle('Categories'));

    // Reactively update columns when hasAccounting changes
    effect(() => {
      this.updateColumnsBasedOnAccounting();
    });

    // Initialize toggle state from URL if present
    const hideInvisibleParam = this.route.snapshot.queryParams['hidden'];
    if (hideInvisibleParam !== undefined) {
      this.showInvisibleCategories.set(hideInvisibleParam === 'true');
    }
  }

  private updateColumnsBasedOnAccounting(): void {
    const hasAcct = this.hasAccounting();
    const newColumns = hasAcct ? hasAccountingCategoryColumns : noAccountingCategoryColumns;

    // Only update if columns actually changed
    const currentColumnsLength = this.columns.filter((col) => col.prop !== '$$expandColumn').length;
    if (currentColumnsLength !== newColumns.length) {
      // Defer column update to avoid ExpressionChangedAfterItHasBeenCheckedError
      queueMicrotask(() => {
        this.columns = [...newColumns];

        // Re-apply templates if they were already initialized
        if (this.columnsInitialized()) {
          this.applyTemplates();
        }
      });
    }
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.busySyncing$.subscribe({
        next: (res) => {
          this.busySyncing.set(res);
        },
      }),
    );

    this.subscriptions.add(
      this.syncStatus$.subscribe({
        next: (data) => {
          if (data) {
            this.syncStatus.set(data);
            if (this.syncStatus() === AccountingSyncStatus.Busy && !this.busySyncing()) {
              this.store.dispatch(new StartPollingCategoriesSyncStatus());
            }
            if (this.syncStatus() === AccountingSyncStatus.Done) {
              this.table?.reloadData();
            }
            if (this.syncStatus() === AccountingSyncStatus.Fail) {
              this.table?.reloadData();
            }
          }
        },
      }),
    );

    this.subscriptions.add(
      this.transactionsState.stopPolling$.subscribe({
        next: () => {
          this.busySyncing.set(false);
        },
      }),
    );
  }

  ngAfterViewInit() {
    this.setupColumns();
  }

  private setupColumns(): void {
    // Guard: only run if templates are available
    if (!this.accountCell || !this.categoryNameCell || !this.actionsCell) {
      return;
    }

    this.applyTemplates();

    // Reassign array to trigger change detection in the table (first time only)
    this.columns = [...this.columns];
    this.columnsInitialized.set(true);
  }

  private applyTemplates(): void {
    for (const column of this.columns) {
      if (column.prop === 'account') column.cellTemplate = this.accountCell;
      if (column.prop === 'name') column.cellTemplate = this.categoryNameCell;
      if (column.prop === 'actions') column.cellTemplate = this.actionsCell;
    }
  }

  getData(queryParams: HttpParams): Observable<PaginatedResults<TransactionCategory>> {
    return this.transactionsApiService.getTransactionCategoriesPaginated(queryParams);
  }

  editCategory(category: TransactionCategory) {
    this.editingRowId.set(category.id);
    const modalRef = this.modalService.open(ManageCategoryComponent, {
      centered: true,
      size: 'md',
      backdrop: 'static',
      keyboard: false,
    });
    modalRef.componentInstance.categoryId = category.id;
    modalRef.closed.subscribe({
      next: () => {
        this.table?.reloadData();
      },
    });
  }

  // Add Category
  addCategory() {
    const modalRef = this.modalService.open(ManageCategoryComponent, {
      centered: true,
      size: 'md',
      backdrop: 'static',
      keyboard: false,
    });
    modalRef.closed.subscribe({
      next: () => {
        this.table?.reloadData();
      },
    });
  }

  toggleVisibility(category: TransactionCategory) {
    const isVisible = category.is_visible;
    const data: TransactionCategoryVisibilityRequest = {
      id: category.id,
      is_visible: !isVisible,
    };
    this.transactionsApiService.editTransactionCategoryVisibility(data).subscribe({
      next: () => {
        this.toastService.show(
          `You have ${isVisible ? 'disabled' : 'enabled'} '${category.name}' category.`,
          'iconoir-check-circle',
        );
      },
      complete: () => {
        this.table?.reloadData();
      },
    });
  }

  onToggleHideInVisible() {
    this.showInvisibleCategories.set(!this.showInvisibleCategories());

    const existingParams = this.route.snapshot.queryParams;
    let queryParams = new HttpParams().appendAll(existingParams);
    queryParams = queryParams.set('hidden', this.showInvisibleCategories().toString());

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: updateParamsObject(queryParams),
    });
  }

  syncCategories() {
    this.busySyncing.set(true);

    // Clear and start polling BEFORE making the API call
    // This ensures polling begins immediately, not after the PUT request completes
    this.store.dispatch(new ClearCategoriesSyncStatus());
    this.store.dispatch(new StartPollingCategoriesSyncStatus());

    this.subscriptions.add(
      this.accountingApiService.requestAccountingSync(AccountingSyncType.Accounts).subscribe({
        next: () => {
          // Sync request initiated successfully - polling will track the status
        },
        error: (error) => {
          this.busySyncing.set(false);
          this.toastService.show(
            `Something went wrong. ${error.error?.non_field_errors ? error.error?.non_field_errors : ''}`,
            'iconoir-warning-triangle',
          );
          this.store.dispatch(new SetAccountingProviderStateFromTransactionSyncFailure());
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
